import { Download, RefreshCw } from "lucide-react";
import { AdminState } from "../components/admin/AdminState";
import { PageHeader } from "../components/admin/PageHeader";
import { StatCard } from "../components/admin/StatCard";
import { StatusBadge } from "../components/admin/StatusBadge";
import { adminDownload, type AdminMetric, useAdminQuery } from "../components/admin/adminApi";

type AnalyticsResponse = {
  funnel: Array<{ label: string; value: number }>;
  metrics: AdminMetric[];
  ok: true;
  statusMix: Array<{ label: string; value: number }>;
};

export function AnalyticsPage() {
  const analytics = useAdminQuery<AnalyticsResponse>("/api/admin/analytics");
  const maxFunnel = Math.max(...(analytics.data?.funnel.map((point) => point.value) ?? [0]), 1);

  return (
    <div className="wr-page wr-analytics-page">
      <PageHeader
        eyebrow="Performance intelligence"
        title="Analytics"
        description="Review funnel, channel delivery, widget interactions, incentive cost, and referral performance."
        meta={
          <>
            <span>Delivery funnel</span>
            <span>Moderation mix</span>
            <span>Exportable CSV</span>
          </>
        }
        action={
          <div className="wr-header-actions">
            <button className="wr-button wr-button--secondary" type="button" onClick={() => void analytics.reload()}>
              <RefreshCw size={15} aria-hidden />
              Refresh
            </button>
            <button className="wr-button" type="button" onClick={() => void adminDownload("/api/admin/exports/reviews.csv", "whatstar-reviews.csv")}>
              <Download size={15} aria-hidden />
              Export CSV
            </button>
          </div>
        }
      />
      <AdminState
        status={analytics.status}
        error={analytics.error}
        onRetry={() => void analytics.reload()}
        emptyText="Analytics appear after requests, messages, reviews, and widget events are recorded."
      >
        <section className="wr-stats-grid wr-analytics-page__metrics" aria-label="Analytics summary">
          {analytics.data?.metrics.map((metric) => (
            <StatCard key={metric.label} {...metric} />
          ))}
        </section>
        <section className="wr-analytics-page__reports">
          <div className="wr-panel wr-analytics-panel">
            <div className="wr-analytics-panel__heading">
              <div>
                <h2>Review funnel</h2>
                <p>Where customers progress from an invitation to submitted proof.</p>
              </div>
              <span>{analytics.data?.funnel.length ?? 0} stages</span>
            </div>
            <div className="wr-chart" role="img" aria-label="Review funnel chart">
              {analytics.data?.funnel.map((point) => (
                <div className="wr-chart__column" key={point.label}>
                  <strong>{point.value.toLocaleString()}</strong>
                  <span style={{ height: `${Math.max((point.value / maxFunnel) * 100, 4)}%` }} />
                  <small>{point.label}</small>
                </div>
              ))}
            </div>
          </div>
          <div className="wr-panel wr-analytics-panel">
            <div className="wr-analytics-panel__heading">
              <div>
                <h2>Moderation mix</h2>
                <p>Current review states needing your attention or ready for storefront proof.</p>
              </div>
              <span>{analytics.data?.statusMix.reduce((total, item) => total + item.value, 0) ?? 0} reviews</span>
            </div>
            <ul className="wr-action-list wr-action-list--analytics">
              {analytics.data?.statusMix.length ? (
                analytics.data.statusMix.map((item) => (
                  <li key={item.label}>
                    <StatusBadge tone="info">{item.value}</StatusBadge>
                    {item.label}
                  </li>
                ))
              ) : (
                <li>No reviews recorded yet.</li>
              )}
            </ul>
          </div>
        </section>
      </AdminState>
    </div>
  );
}
